import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';

export default function RolesPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('roles', 'create');

  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api.get('/roles').then(r => r.data.items) });
  const permissions = useQuery({ queryKey: ['permissions'], queryFn: () => api.get('/roles/permissions').then(r => r.data.items) });

  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);   // role being edited or null for create
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [error, setError] = useState(null);

  const open = (role) => {
    setEditing(role);
    setName(role?.name || '');
    setDescription(role?.description || '');
    setSelected(new Set(role?.permissions || []));
    setError(null);
    setShow(true);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description: description || null, permissions: Array.from(selected) };
      return editing
        ? api.patch(`/roles/${editing.id}`, body).then(r => r.data)
        : api.post('/roles', body).then(r => r.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['roles'] }); setShow(false); },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data))
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['roles'] })
  });

  // Group permissions by resource for nicer rendering
  const grouped = useMemo(() => {
    const map = {};
    for (const p of permissions.data || []) {
      (map[p.resource] ||= []).push(p);
    }
    return map;
  }, [permissions.data]);

  return (
    <>
      <PageHeader title="Rollen" subtitle="Zuordnung von Permissions zu wiederverwendbaren Bündeln"
        actions={canWrite && <Button onClick={() => open(null)}><Icon icon="tabler:plus" className="me-1" />Neue Rolle</Button>} />

      <Card className="card-flat">
        <Card.Body className="p-0">
          <Table hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr><th>Name</th><th>Beschreibung</th><th>Permissions</th><th>Typ</th><th></th></tr>
            </thead>
            <tbody>
              {(roles.data || []).map(r => (
                <tr key={r.id}>
                  <td className="fw-semibold">{r.name}</td>
                  <td className="text-muted">{r.description || '—'}</td>
                  <td>
                    {r.permissions.length > 6
                      ? <span className="text-muted">{r.permissions.length} Permissions</span>
                      : r.permissions.map(p => <code key={p} className="small me-2">{p}</code>)}
                  </td>
                  <td>{r.is_system
                    ? <Badge bg="" className="badge-soft-secondary">system</Badge>
                    : <Badge bg="" className="badge-soft-primary">custom</Badge>}</td>
                  <td className="text-end pe-3">
                    {canWrite && (
                      <>
                        <Button size="sm" variant="outline-secondary" onClick={() => open(r)} className="me-1"
                          title={r.is_system ? 'System-Rolle: Beschreibung + Permissions editierbar' : 'Bearbeiten'}>
                          Bearbeiten
                        </Button>
                        <Button size="sm" variant="outline-danger"
                          disabled={r.name === 'SuperAdmin' || r.name === 'Self'}
                          title={(r.name === 'SuperAdmin' || r.name === 'Self')
                            ? 'Geschützte System-Rolle — kann nicht gelöscht werden'
                            : 'Löschen'}
                          onClick={() => confirm(`Rolle "${r.name}" wirklich löschen?`) && del.mutate(r.id)}>
                          Löschen
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={show} onHide={() => setShow(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? `Rolle bearbeiten: ${editing.name}` : 'Neue Rolle'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          <Row className="g-3 mb-3">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Name *</Form.Label>
                <Form.Control required value={name} onChange={(e) => setName(e.target.value)}
                  disabled={editing && (editing.name === 'SuperAdmin' || editing.name === 'Self')} />
                {editing && (editing.name === 'SuperAdmin' || editing.name === 'Self') && (
                  <Form.Text>System-Rolle — Name nicht änderbar.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={7}>
              <Form.Group>
                <Form.Label>Beschreibung</Form.Label>
                <Form.Control value={description} onChange={(e) => setDescription(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          <h6 className="text-muted text-uppercase small mt-3">Permissions</h6>
          {Object.entries(grouped).map(([resource, items]) => (
            <div key={resource} className="mb-3">
              <div className="fw-semibold mb-1">{resource}</div>
              <div className="d-flex flex-wrap gap-2">
                {items.map(p => (
                  <Form.Check key={p.id} type="checkbox" id={`p-${p.id}`}
                    label={<span><code className="small">{p.scope}:{p.action}</code> <span className="text-muted">— {p.label}</span></span>}
                    checked={selected.has(p.permission_string)}
                    onChange={(e) => {
                      const s = new Set(selected);
                      if (e.target.checked) s.add(p.permission_string); else s.delete(p.permission_string);
                      setSelected(s);
                    }} />
                ))}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>Abbrechen</Button>
          <Button onClick={() => save.mutate()} disabled={!name || save.isLoading}>
            {save.isLoading ? 'Speichere…' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
